/**
 * WeaponShopSystem - Sistema de tienda, discovery y loadout de armas
 * Uses DataManagerWeapon as single source of truth for weapon data
 */

import { storage, WeaponState, SlotLoadout } from '../core/Storage';
import { DataManagerWeapon } from '../weapons/DataManagerWeapon';
import type {
  WeaponDefinition,
  ShopConfig,
  SlotConfig,
} from '../weapons/DataManagerWeapon';

export type SlotType = 'gun' | 'tractor' | 'bomb';

// Re-export WeaponConfig type for consumers (maps to WeaponDefinition)
export type WeaponConfig = WeaponDefinition;

class WeaponShopSystem {
  private state: WeaponState | null = null;

  // ========== INITIALIZATION ==========

  async init(): Promise<void> {
    this.state = await storage.getWeaponState();
  }

  async refresh(): Promise<void> {
    this.state = await storage.getWeaponState();
  }

  // ========== GETTERS ==========

  getConfig(): ShopConfig | null {
    return DataManagerWeapon.getShopConfig();
  }

  getWeaponConfig(weaponId: string): WeaponConfig | undefined {
    return DataManagerWeapon.get(weaponId);
  }

  getSlotConfig(slot: SlotType): SlotConfig | undefined {
    return DataManagerWeapon.getSlotConfig(slot);
  }

  getSwapCost(): number {
    return DataManagerWeapon.getSwapCost();
  }

  getMaxWeaponsPerSlot(): number {
    return DataManagerWeapon.getMaxWeaponsPerSlot();
  }

  // ========== STATE ==========

  getState(): WeaponState | null {
    return this.state;
  }

  isDiscovered(weaponId: string): boolean {
    return this.state?.discovered.includes(weaponId) ?? false;
  }

  getDiscoveredWeapons(): string[] {
    return this.state?.discovered ?? [];
  }

  getDiscoveredForSlot(slot: SlotType): WeaponConfig[] {
    const discovered = this.state?.discovered ?? [];
    return discovered
      .map(id => DataManagerWeapon.get(id))
      .filter((w): w is WeaponDefinition => w !== undefined && w.slot === slot);
  }

  getAmmo(weaponId: string): number {
    return this.state?.ammo[weaponId] ?? 0;
  }

  getLoadout(slot: SlotType): SlotLoadout {
    return this.state?.loadout[slot] ?? { equipped: [], activeIndex: 0 };
  }

  getActiveWeapon(slot: SlotType): WeaponConfig | null {
    const loadout = this.getLoadout(slot);
    if (loadout.equipped.length === 0) return null;
    const weaponId = loadout.equipped[loadout.activeIndex];
    return DataManagerWeapon.get(weaponId) ?? null;
  }

  getEquippedWeapons(slot: SlotType): WeaponConfig[] {
    const loadout = this.getLoadout(slot);
    return loadout.equipped
      .map(id => DataManagerWeapon.get(id))
      .filter((w): w is WeaponDefinition => w !== undefined);
  }

  // ========== DISCOVERY ==========

  async discoverWeapon(weaponId: string): Promise<boolean> {
    const weapon = DataManagerWeapon.get(weaponId);
    if (!weapon) return false;

    if (this.isDiscovered(weaponId)) return false;

    this.state = await storage.discoverWeapon(weaponId, weapon.initialAmmo);
    return true;
  }

  // ========== LOADOUT MANAGEMENT ==========

  async addToLoadout(slot: SlotType, weaponId: string): Promise<boolean> {
    if (!this.state) return false;

    const weapon = DataManagerWeapon.get(weaponId);
    if (!weapon || weapon.slot !== slot) return false;

    if (!this.isDiscovered(weaponId)) return false;

    const loadout = this.state.loadout[slot];
    if (loadout.equipped.includes(weaponId)) return false;

    if (loadout.equipped.length >= DataManagerWeapon.getMaxWeaponsPerSlot()) return false;

    const newEquipped = [...loadout.equipped, weaponId];
    this.state = await storage.setSlotLoadout(slot, newEquipped, loadout.activeIndex);
    return true;
  }

  async removeFromLoadout(slot: SlotType, weaponId: string): Promise<boolean> {
    if (!this.state) return false;

    const weapon = DataManagerWeapon.get(weaponId);
    if (!weapon) return false;

    // Cannot remove base weapon if it's the only one
    const loadout = this.state.loadout[slot];
    if (loadout.equipped.length <= 1) return false;

    const newEquipped = loadout.equipped.filter(id => id !== weaponId);
    if (newEquipped.length === loadout.equipped.length) return false;

    // Adjust active index if needed
    let newActiveIndex = loadout.activeIndex;
    if (newActiveIndex >= newEquipped.length) {
      newActiveIndex = newEquipped.length - 1;
    }

    this.state = await storage.setSlotLoadout(slot, newEquipped, newActiveIndex);
    return true;
  }

  async setActiveWeaponByIndex(slot: SlotType, index: number): Promise<boolean> {
    if (!this.state) return false;

    const loadout = this.state.loadout[slot];
    if (index < 0 || index >= loadout.equipped.length) return false;

    this.state = await storage.setActiveWeapon(slot, index);
    return true;
  }

  // ========== WEAPON SWAP (IN-GAME) ==========

  async swapWeapon(slot: SlotType, direction: 'next' | 'prev'): Promise<{ success: boolean; cost: number; newWeapon: WeaponConfig | null }> {
    if (!this.state) {
      return { success: false, cost: 0, newWeapon: null };
    }

    const loadout = this.state.loadout[slot];
    if (loadout.equipped.length <= 1) {
      return { success: false, cost: 0, newWeapon: null };
    }

    const cost = DataManagerWeapon.getSwapCost();
    const currentBiomatter = await storage.getBiomatter();

    if (currentBiomatter < cost) {
      return { success: false, cost: 0, newWeapon: null };
    }

    // Calculate new index
    let newIndex = loadout.activeIndex;
    if (direction === 'next') {
      newIndex = (newIndex + 1) % loadout.equipped.length;
    } else {
      newIndex = (newIndex - 1 + loadout.equipped.length) % loadout.equipped.length;
    }

    // Deduct cost and update
    await storage.saveBiomatter(currentBiomatter - cost);
    this.state = await storage.setActiveWeapon(slot, newIndex);

    const newWeapon = DataManagerWeapon.get(loadout.equipped[newIndex]) ?? null;
    return { success: true, cost, newWeapon };
  }

  // ========== AMMO PURCHASE ==========

  async buyAmmo(weaponId: string): Promise<{ success: boolean; ammoAdded: number; cost: number; newTotal: number }> {
    const weapon = DataManagerWeapon.get(weaponId);
    if (!weapon || weapon.hasInfiniteAmmo) {
      return { success: false, ammoAdded: 0, cost: 0, newTotal: 0 };
    }

    if (!this.isDiscovered(weaponId)) {
      return { success: false, ammoAdded: 0, cost: 0, newTotal: 0 };
    }

    const cost = weapon.packCost ?? 0;
    const packSize = weapon.packSize ?? 0;

    if (cost === 0 || packSize === 0) {
      return { success: false, ammoAdded: 0, cost: 0, newTotal: 0 };
    }

    const currentBiomatter = await storage.getBiomatter();
    if (currentBiomatter < cost) {
      return { success: false, ammoAdded: 0, cost: 0, newTotal: 0 };
    }

    // Deduct cost and add ammo
    await storage.saveBiomatter(currentBiomatter - cost);
    const newTotal = await storage.addAmmo(weaponId, packSize);

    // Refresh state
    await this.refresh();

    return { success: true, ammoAdded: packSize, cost, newTotal };
  }

  async buyAmmoMultiple(weaponId: string, times: number): Promise<{ success: boolean; ammoAdded: number; cost: number; newTotal: number }> {
    const weapon = DataManagerWeapon.get(weaponId);
    if (!weapon || weapon.hasInfiniteAmmo) {
      return { success: false, ammoAdded: 0, cost: 0, newTotal: 0 };
    }

    if (!this.isDiscovered(weaponId)) {
      return { success: false, ammoAdded: 0, cost: 0, newTotal: 0 };
    }

    const costPerPack = weapon.packCost ?? 0;
    const packSize = weapon.packSize ?? 0;
    const totalCost = costPerPack * times;
    const totalAmmo = packSize * times;

    if (totalCost === 0 || totalAmmo === 0) {
      return { success: false, ammoAdded: 0, cost: 0, newTotal: 0 };
    }

    const currentBiomatter = await storage.getBiomatter();
    if (currentBiomatter < totalCost) {
      return { success: false, ammoAdded: 0, cost: 0, newTotal: 0 };
    }

    // Deduct cost and add ammo
    await storage.saveBiomatter(currentBiomatter - totalCost);
    const newTotal = await storage.addAmmo(weaponId, totalAmmo);

    // Refresh state
    await this.refresh();

    return { success: true, ammoAdded: totalAmmo, cost: totalCost, newTotal };
  }

  // ========== AMMO USAGE ==========

  async useAmmo(weaponId: string, amount: number = 1): Promise<boolean> {
    const weapon = DataManagerWeapon.get(weaponId);
    if (!weapon) return false;

    // Infinite ammo weapons don't consume
    if (weapon.hasInfiniteAmmo) return true;

    const success = await storage.useAmmo(weaponId, amount);
    if (success) {
      await this.refresh();
    }
    return success;
  }

  canFire(weaponId: string): boolean {
    const weapon = DataManagerWeapon.get(weaponId);
    if (!weapon) return false;

    if (weapon.hasInfiniteAmmo) return true;

    return this.getAmmo(weaponId) > 0;
  }

  // ========== HELPERS ==========

  getAllWeapons(): WeaponConfig[] {
    return DataManagerWeapon.getIds().map(id => DataManagerWeapon.get(id)!);
  }

  getWeaponsForSlot(slot: SlotType): WeaponConfig[] {
    return DataManagerWeapon.getWeaponsForSlot(slot);
  }

  getBaseWeapon(slot: SlotType): WeaponConfig | undefined {
    return DataManagerWeapon.getBaseWeaponForSlot(slot);
  }

  isInLoadout(slot: SlotType, weaponId: string): boolean {
    const loadout = this.getLoadout(slot);
    return loadout.equipped.includes(weaponId);
  }
}

// Singleton
export const weaponShopSystem = new WeaponShopSystem();
